// Components
import { DashboardFilter, HeaderMenu, MultipleChart } from "components"
import { SummaryCard } from "./components"
import uniqolor from "uniqolor"
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import 'chartjs-plugin-datalabels'
import { convertNumber } from "utils"
import { useState } from "react"
import moment from "moment"
import { useList } from "./hooks"

ChartJS.register(ArcElement, ChartDataLabels, Tooltip)

export default function Dashboard() {
	// Vars
	const agedReceivableList: string[] = ["7 DAYS", "15 DAYS", "21 DAYS", "30 DAYS", "60 DAYS"]

	const [fill, setFill] = useState<{ month: string, year: string, location_id?: string }>({
		month: moment().format("MM"),
		year: moment().format("YYYY")
	})

	const { data, loading } = useList({
		yearMonth: `${fill.year}${fill.month}`,
		location_id: fill.location_id ?? ""
	})

	const mappedAgedReceivable = agedReceivableList.map(label => {
		const key = label.toLowerCase().replace(" ", "");
		return {
			label,
			value: data?.aged_receivable[(key as any)] ?? 0
		};
	});

	const labels = data?.sales_by_location?.map((item) => item.location_name) || []
	const dataValue = data?.sales_by_location?.map((item) => item.jrevenue) || []
	const totalValue = data?.sales_by_location?.reduce((acc, item) => acc + Number(item.jrevenue), 0)

	return (
		<section className="container my-5">
			<section className="flex flex-col gap-3">
				<HeaderMenu title="SALES DASHBOARD" />
				<DashboardFilter onChangeFilter={(res) => {
					setFill({
						month: moment(res.date_from).format("MM"),
						year: moment(res.date_from).format("YYYY"),
						location_id: res.location_id
					})
				}} />

				<section className="flex flex-col gap-3">
					<section className="grid 2xl:grid-cols-2 gap-3">
						<section className="grid grid-cols-2 gap-3">
							<section className="flex flex-col gap-y-1">
								<section className="border border-black grid grid-cols-2">
									<SummaryCard
										label="NUMBER OF SALES"
										sideLabel="CURRENT"
										value={data?.current_number_invoice ? `${convertNumber(data?.current_number_invoice).withoutDecimal} INVOICES` : "0 INVOICES"}
										loading={loading}
									/>

									<SummaryCard
										label="NUMBER OF SALES"
										sideLabel="PREV MONTH"
										value={data?.previous_number_invoice ? `${convertNumber(data?.previous_number_invoice).withoutDecimal} INVOICES` : "0 INVOICES"}
										loading={loading}
									/>
								</section>

								<section className="border border-black grid grid-cols-2">
									<SummaryCard
										label="NUMBER OF NEW CUSTOMERS"
										value={`${convertNumber(data?.new_customer  ?? 0).withoutDecimal} CUSTOMERS`}
									/>

									<SummaryCard
										label="NUMBER OF OLD CUSTOMERS"
										value={`${convertNumber(data?.old_customer  ?? 0).withoutDecimal} CUSTOMERS`}
									/>
								</section>
							</section>

							<section className="flex flex-col gap-y-1">
								<section className="border border-black grid grid-cols-2">
									<SummaryCard
										label="REVENUE"
										sideLabel="CURRENT"
										value={data?.current_revenue ? `Rp ${convertNumber(data?.current_revenue).intoCurrency}` : "RP 0"}
										loading={loading}

									/>

									<SummaryCard
										label="REVENUE"
										sideLabel="PREV MONTH"
										value={data?.previous_revenue ? `Rp ${convertNumber(data?.previous_revenue).intoCurrency}` : "RP 0"}
										loading={loading}
									/>
								</section>

								<section className="border border-black grid grid-cols-2">
									<SummaryCard
										label="AVERAGE INVOICE"
										sideLabel="CURRENT"
										value={data?.current_average_invoice ? `Rp ${convertNumber(data?.current_average_invoice).intoCurrency}` : "RP 0"}
										loading={loading}
									/>

									<SummaryCard
										label="AVERAGE INVOICE"
										sideLabel="PREV MONTH"
										value={data?.previous_average_invoice ? `Rp ${convertNumber(data?.previous_average_invoice).intoCurrency}` : "RP 0"}
										loading={loading}
									/>
								</section>
							</section>
						</section>

						<section className="grid grid-cols-2 gap-3">
							<section className="flex flex-col gap-y-1">
								<section className="border border-black grid grid-cols-2">
									<SummaryCard
										label="COST OF SALES"
										sideLabel="CURRENT"
										value={data?.current_cost_of_sales ? `Rp ${convertNumber(data?.current_cost_of_sales).intoCurrency}` : "RP 0"}
										loading={loading}
									/>

									<SummaryCard
										label="COST OF SALES"
										sideLabel="PREV MONTH"
										value={data?.previous_cost_of_sales ? `Rp ${convertNumber(data?.previous_cost_of_sales).intoCurrency}` : "RP 0"}
										loading={loading}
									/>
								</section>

								<section className="border border-black grid grid-cols-4">
									<SummaryCard label="MARKET SHARE" />

									<SummaryCard
										label="RETAIL"
										value={`${data?.market_share_retail ? convertNumber(data?.market_share_retail).intoNormalAmount : "0"}%`}
										loading={loading}
									/>

									<SummaryCard
										label="WHOLESALES"
										value={`${data?.market_share_wholesales ? convertNumber(data?.market_share_wholesales).intoNormalAmount : "0"}%`}
										loading={loading}
									/>

									<SummaryCard
										label="SPECIAL"
										value={`${data?.market_share_special ? convertNumber(data?.market_share_special).intoNormalAmount : "0"}%`}
										loading={loading}
									/>
								</section>
							</section>

							<section className="flex flex-col gap-y-1">
								<section className="border border-black grid grid-cols-2">
									<SummaryCard
										label="PROFIT BY SALE"
										sideLabel="CURRENT"
										value={data?.current_profit_by_sales ? `Rp ${convertNumber(data?.current_profit_by_sales).intoCurrency}` : "RP 0"}
										loading={loading}
									/>

									<SummaryCard
										label="PROFIT BY SALE"
										sideLabel="PREV MONTH"
										value={data?.previous_profit_by_sales ? `Rp ${convertNumber(data?.previous_profit_by_sales).intoCurrency}` : "RP 0"}
										loading={loading}
									/>
								</section>

								<section className="border border-black grid grid-cols-3">
									<SummaryCard
										label="NUMBER OF QUOTATION"
										value={`${convertNumber(data?.number_of_quotation ?? 0).withoutDecimal}`}
										loading={loading}
									/>

									<SummaryCard
										label="NUMBER OF ORDER"
										value={`${convertNumber(data?.number_of_order ?? 0).withoutDecimal}`}
										loading={loading}
									/>

									<SummaryCard
										label="NUMBER OF RETURN"
										value={`${convertNumber(data?.number_of_return ?? 0).withoutDecimal}`}
										loading={loading}
									/>
								</section>
							</section>
						</section>

						<section className="grid grid-cols-2 gap-3">
							<div className="border border-black p-3">
								<div className="font-bold mb-2">SALES BY LOCATION</div>

								<MultipleChart
									className="h-[270px]"
									chartType="bar"
									data={{
										labels: labels,
										datasets: [
											{
												data: dataValue,
												backgroundColor: labels.map(() => uniqolor.random({ format: "rgb" }).color),
												borderColor: ['rgb(167, 230, 255)'],
											}
										]
									}}
								/>
							</div>

							<div className="border border-black p-3">
								<div className="font-bold mb-2">AGED RECEIVABLE</div>

								<MultipleChart
									className="h-[270px]"
									chartType="bar"
									data={{
										labels: mappedAgedReceivable.map((item) => item.label),
										datasets: [
											{
												data: mappedAgedReceivable.map((item) => Number(item.value)),
												backgroundColor: mappedAgedReceivable.map(() => uniqolor.random({ format: "rgb" }).color),
												borderColor: ['rgb(167, 230, 255)'],
											}
										]
									}}
								/>
							</div>
						</section>


						<section className="grid grid-cols-2 gap-3">
							<section className="grid grid-cols-5 gap-1">
								<div className="lg:col-span-5 col-span-2 border border-black p-3">
									<div className="font-bold mb-2">COMPARATION TOTAL SALES</div>

									<MultipleChart
										chartType="bar"
										data={{
											labels: data?.compare_total_sales?.flatMap(item => Object.keys(item).map(key => moment(key, "YYYYMM").format("MMMM YYYY"))) || [],
											datasets: [
												{
													data: data?.compare_total_sales?.flatMap((item) => Object.values(item)) || [],
												}
											]
										}}
									/>
								</div>

								<div className="border border-black flex flex-col justify-center items-center lg:col-span-1 lg:w-[130px]">
									<SummaryCard
										label="CMP"
										value={`${ data?.cmp ? convertNumber(data?.cmp).intoCurrency : "0"} %`}
										loading={loading}
									/>
								</div>

								<div className="border border-black flex flex-col justify-center items-center lg:col-span-1 lg:ml-14 lg:w-[130px]">
									<SummaryCard
										label="OPERATING EXPENSES"
										value={`${data?.operating_expences ? convertNumber(data?.operating_expences).intoNormalAmount : "0"} %`}
										loading={loading}
									/>
								</div>

								<div className="border border-black flex flex-col justify-center items-center lg:col-span-1 lg:ml-28 lg:w-[130px]">
									<SummaryCard
										label="QUANTITY UNIT SOLD"
										value={`${data?.quantity_unit_sold ? convertNumber(data?.quantity_unit_sold).intoNormalAmount : "0"}`}
										loading={loading}
									/>
								</div>
							</section>
							<div className="border border-black p-3">
								<section className="grid grid-cols-2 gap-1 items-center">
									<section className="flex justify-end max-w-[400px] aspect-square">
										<MultipleChart
											chartType="doughnut"
											className="w-full h-full"
											data={{
												labels: labels,
												datasets: [
													{
														data: data?.sales_by_location?.map((item) => (totalValue ? (item.jrevenue / totalValue) * 100 : 0 )) || [],
													}
												]
											}}
											options={{
												plugins: {
													legend: { display: false },
													datalabels: {
														font: { size: 8 },
														formatter: (value, context) => `${labels[context.dataIndex]} \n ${value.toFixed(2)} %`
													}
												}
											}}
										/>
									</section>

									<section className="flex flex-col justify-center font-bold text-right">
										<div >TOTAL SALES</div>
										<div className="mb-2 text-sm ">{data?.current_revenue ? convertNumber(data?.current_revenue).intoCurrency : "0"}</div>
										<div>BUDGET</div>
										<div className="mb-2 text-sm ">{data?.budget ? convertNumber(data?.budget).intoCurrency : "0"}</div>
										<div>EXPENSES</div>
										<div className="mb-2 text-sm ">{data?.expense ? convertNumber(data?.expense).intoCurrency : "0"}</div>
									</section>
								</section>
							</div>
						</section>
					</section>

					<div className="min-h-[200px] border border-black px-5 py-3">
						<div className="font-bold">SALES REP</div>
					</div>
				</section>
			</section>
		</section>
	)
}